import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'

const shiftLabel = (shift) => {
  if (!shift) return ''
  if (shift.event?.title) return shift.event.title
  if (shift.roster?.title) return shift.roster.title
  if (shift.ptsDuty?.dutyRef) return `PTS ${shift.ptsDuty.dutyRef}`
  return shift.shiftType || 'Shift'
}

const shiftTimes = (shift) =>
  `${moment(shift.start).format('ddd D MMM HH:mm')} – ${moment(shift.end).format('HH:mm')}`

const toLocalInput = (date) => moment(date).format('YYYY-MM-DDTHH:mm')

// Warning shown when a dispatcher crews someone who isn't on duty: surfaces
// their booked shifts and offers either adding them anyway (they clock in to
// an existing shift later) or booking a shift for them right now — event
// rooms get an event shift, ad-hoc rooms an admin shift on the rota.
const OffDutyCrewDialog = ({
  open,
  onClose,
  staffName,
  status,
  onAddAndWait,
  onCreateShift,
}) => {
  const [creatingShift, setCreatingShift] = useState(false)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setCreatingShift(false)
    setBusy(false)
    setError('')
    setStart(toLocalInput(new Date()))
    setEnd(toLocalInput(moment().add(8, 'hours')))
  }, [open])

  const currentShift = status?.currentShift
  const nextShift = status?.nextShift
  const hasStaffRecord = status?.hasStaffRecord !== false

  const runAction = (action) => {
    setBusy(true)
    setError('')
    action()
      .then((result) => {
        if (result?.ok) {
          onClose()
        } else {
          setError(result?.error || 'Something went wrong. Try again.')
        }
      })
      .catch(() => setError('Something went wrong. Try again.'))
      .finally(() => setBusy(false))
  }

  const submitCreateShift = () => {
    if (!start || !end || moment(end).isSameOrBefore(moment(start))) {
      setError('Enter a valid start and end time.')
      return
    }
    runAction(() =>
      onCreateShift({
        start: moment(start).toISOString(),
        end: moment(end).toISOString(),
      }),
    )
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, color: '#242F40' }}>
        Not currently on duty
      </DialogTitle>

      <DialogContent>
        <Stack spacing={1.5}>
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            {staffName || 'This person'} isn&apos;t clocked in to a shift right
            now.
          </Alert>

          {currentShift ? (
            <Typography variant="body2" color="text.secondary">
              They&apos;re booked on <b>{shiftLabel(currentShift)}</b> (
              {shiftTimes(currentShift)}) but haven&apos;t clocked in yet.
            </Typography>
          ) : nextShift ? (
            <Typography variant="body2" color="text.secondary">
              Their next booked shift is <b>{shiftLabel(nextShift)}</b> —{' '}
              {shiftTimes(nextShift)}.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              They have no upcoming shifts booked.
            </Typography>
          )}

          {!hasStaffRecord && (
            <Typography variant="body2" color="text.secondary">
              This account has no staff record, so a shift can&apos;t be booked
              for them.
            </Typography>
          )}

          {creatingShift && (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                A shift will be created and added to the rota — linked to this
                control room&apos;s event where there is one.
              </Typography>
              <TextField
                label="Shift start"
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Shift end"
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
        >
          Cancel
        </Button>

        {creatingShift ? (
          <Button
            variant="contained"
            disabled={busy}
            onClick={submitCreateShift}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
              '&:hover': { backgroundColor: '#1E7F88' },
            }}
          >
            Create Shift &amp; Add
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => runAction(onAddAndWait)}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
            >
              Add &amp; Wait for Clock-In
            </Button>
            {hasStaffRecord && (
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => setCreatingShift(true)}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#239AA4',
                  '&:hover': { backgroundColor: '#1E7F88' },
                }}
              >
                Create a Shift…
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default OffDutyCrewDialog
